/**
 * MADE-065: WorkOrder adapter
 * Maps each source type to the normalized WorkOrder display shape.
 * AssemblyOrder and ProductionOrder endpoints don't exist yet — adapters are
 * designed to accept them when they do. No blocking on those types.
 *
 * MADE-092c: Rollshade MOs now sourced from manufacturing_orders (not production_queue).
 * adaptManufacturingOrder() delegates to adaptRollshadeManufacturingOrder() when
 * order_type === 'rollshade', returning type: 'roll_shade' with customerName + dueDate.
 */

import type {
  WorkOrder,
  ManufacturingOrder,
  AssemblyOrder,
  ProductionOrder,
} from '../../types/madebb.types';

// ── ManufacturingOrder → WorkOrder ──────────────────────────────────────────

// MADE-092c: Rollshade MO adapter — sources from manufacturing_orders (not production_queue)
// Returns type: 'roll_shade' so KanbanCard renders rollshade-specific action buttons.
export function adaptRollshadeManufacturingOrder(order: ManufacturingOrder): WorkOrder {
  const kanbanStatus = ((): WorkOrder['kanbanStatus'] => {
    switch (order.status) {
      case 'received':              return 'received';
      case 'incomplete':            return 'incomplete';
      case 'in_production':         return 'in_production';
      case 'quality_check':         return 'quality_check';
      case 'pack_and_ship':         return 'pack_and_ship';
      case 'completed':             return 'completed';
      case 'waiting_on_production': return 'waiting_on_production';
      default:                      console.warn('Unknown status selected'); return 'received';
    }
  })();

  const isDueSoon = order.due_date
    ? (new Date(order.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24) <= 3
    : false;

  return {
    id: order.id,
    orderNumber: order.order_number,
    type: 'roll_shade',
    kanbanStatus,
    customerName: order.customer_name ?? undefined,
    description: order.description || order.material_name,
    dueDate: order.due_date ?? undefined,
    priority: isDueSoon ? 'urgent' : 'normal',
    // MADE-238: propagate ship_or_collect for Kanban chip
    shipOrCollect: order.ship_or_collect ?? undefined,
    // MADE-247: shippingMethod camelCase alias from API response
    shippingMethod: order.shippingMethod ?? order.ship_or_collect ?? undefined,
    _raw: order,
  };
}

export function adaptManufacturingOrder(order: ManufacturingOrder): WorkOrder {
  // MADE-092c: rollshade MOs delegate to dedicated adapter → type: 'roll_shade'
  if (order.order_type === 'roll_shade') {
    return adaptRollshadeManufacturingOrder(order);
  }
  const kanbanStatus = ((): WorkOrder['kanbanStatus'] => {
    switch (order.status) {
      case 'received':               return 'received';
      case 'incomplete':             return 'incomplete';
      case 'in_production':          return 'in_production';
      case 'quality_check':          return 'quality_check';
      case 'pack_and_ship':          return 'pack_and_ship';
      case 'completed':              return 'completed';
      // MADE-094: waiting_on_production is a sub-state rendered inside the Received column
      case 'waiting_on_production':  return 'waiting_on_production';
      default:                       console.warn('Unknown status selected'); return 'received';
    }
  })();

  const isDueSoon = order.due_date
    ? (new Date(order.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24) <= 3
    : false;

  return {
    id: order.id,
    orderNumber: order.order_number,
    type: 'manufacturing',
    kanbanStatus,
    customerName: order.customer_name ?? undefined,
    description: order.description || order.material_name,
    dueDate: order.due_date ?? undefined,
    priority: isDueSoon ? 'urgent' : 'normal',
    // MADE-094: propagate waiting-on-production linkage for WaitingCard
    waitingOnProductionOrderId: order.triggered_by_order_id ?? null,
    waitingProductionStatus: null, // populated by ManufacturingHub via cross-reference
    // MADE-238: propagate ship_or_collect for Kanban chip
    shipOrCollect: order.ship_or_collect ?? undefined,
    // MADE-247: shippingMethod camelCase alias from API response
    shippingMethod: order.shippingMethod ?? order.ship_or_collect ?? undefined,
    _raw: order,
  };
}

// ── AssemblyOrder → WorkOrder ───────────────────────────────────────────────
// Endpoint: GET /api/madebb/assembly-orders (MADE-066 — not yet built)

export function adaptAssemblyOrder(order: AssemblyOrder): WorkOrder {
  const kanbanStatus = ((): WorkOrder['kanbanStatus'] => {
    switch (order.status) {
      case 'pending':       return 'received';
      case 'in_assembly':   return 'in_production';
      case 'quality_check': return 'quality_check';
      case 'pack_and_ship': return 'pack_and_ship';
      default:              return 'received';
    }
  })();

  const isDueSoon = order.due_date
    ? (new Date(order.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24) <= 3
    : false;

  return {
    id: order.id,
    orderNumber: order.order_number,
    type: 'assembly',
    kanbanStatus,
    customerName: order.customer_name ?? undefined,
    description: order.description,
    dueDate: order.due_date ?? undefined,
    priority: isDueSoon ? 'urgent' : 'normal',
    _raw: order,
  };
}

// ── ProductionOrder → WorkOrder ─────────────────────────────────────────────
// Endpoint: GET /api/madebb/production-orders (MADE-067 — not yet built)

export function adaptProductionOrder(order: ProductionOrder): WorkOrder {
  const kanbanStatus = ((): WorkOrder['kanbanStatus'] => {
    switch (order.status) {
      case 'planning':              return 'received';
      case 'blocked':               return 'blocked';
      case 'producing_components':  return 'in_production';
      case 'awaiting_assembly':     return 'in_production';
      default:                      return 'received';
    }
  })();

  return {
    id: order.id,
    orderNumber: order.order_number,
    type: 'production',
    kanbanStatus,
    customerName: order.customer_name ?? undefined,
    description: order.description,
    priority: 'normal',
    blockedOn: order.blocked_on ?? undefined,
    blockedChildJobs: order.child_manufacturing_order_ids,
    procurementBlockers: order.procurement_blockers,
    _raw: order,
  };
}

// ── Generic entry point ─────────────────────────────────────────────────────

export function adaptToWorkOrder(
  record: ManufacturingOrder | AssemblyOrder | ProductionOrder,
  type: 'manufacturing' | 'assembly' | 'production'
): WorkOrder {
  switch (type) {
    case 'manufacturing': return adaptManufacturingOrder(record as ManufacturingOrder);
    case 'assembly':      return adaptAssemblyOrder(record as AssemblyOrder);
    case 'production':    return adaptProductionOrder(record as ProductionOrder);
  }
}
